import React, { useEffect, useMemo, useState } from "react";
import "./TalentRedirection.css";
import { getLogoutUrl } from "./utils/logoutRedirect";
import { ShieldIcon, LinkIcon, CopyIcon, ArrowRightIcon } from "lucide-react";

const TalentRedirection = ({ autoRedirectMs = 10000 }) => {
  const [isAnimated, setIsAnimated] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [copied, setCopied] = useState(false);

  const currentUrl = useMemo(
    () => (typeof window === "undefined" ? "" : window.location.href),
    []
  );

  const newUrl = useMemo(
    () => (typeof window === "undefined" ? "" : getLogoutUrl()),
    []
  );

  const initialSeconds = Math.max(1, Math.ceil(autoRedirectMs / 1000));
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

  useEffect(() => {
    setIsAnimated(true);

    const handleMouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handleMouseMove);

    setSecondsLeft(initialSeconds);

    let redirectTimer;
    let tickTimer;

    if (autoRedirectMs && newUrl) {
      redirectTimer = window.setTimeout(() => {
        window.location.assign(newUrl);
      }, autoRedirectMs);

      tickTimer = window.setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (redirectTimer) window.clearTimeout(redirectTimer);
      if (tickTimer) window.clearInterval(tickTimer);
    };
  }, [autoRedirectMs, newUrl, initialSeconds]);

  const handleGo = () => window.location.assign(newUrl);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      const el = document.createElement("textarea");
      el.value = newUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <div className="auth-main-container moved">
      <div
        className="auth-bg-dynamic"
        style={{
          background: `radial-gradient(320px circle at ${mousePos.x}px ${mousePos.y}px, rgba(14, 24, 34, 0.10), transparent 45%)`,
          opacity: isAnimated ? 1 : 0,
        }}
        aria-hidden="true"
      />

      <div className={`redirect-card ${isAnimated ? "enter" : ""}`}>
        <div className="redirect-badge moved">
          <ShieldIcon />
          <span> You’re using an old link.</span>
        </div>

        <div className="redirect-header">
          <div className="redirect-icon moved">
            <LinkIcon />
          </div>

          <div>
            <h1 className="redirect-title">
              We’ve updated our sign-in experience.
            </h1>
            <p className="redirect-subtitle">
              We've moved to a new sign-in page, but your account and access
              remain the same. You will be redirected shortly.
            </p>
          </div>
        </div>

        <div className="redirect-panel">
          <div className="redirect-row">
            <span className="label">From</span>
            <span className="value" title={currentUrl}>
              {currentUrl || "—"}
            </span>
          </div>

          <div className="divider" />

          <div className="redirect-row">
            <span className="label">To</span>
            <a className="value link" href={newUrl}>
              {newUrl}
            </a>
          </div>

          <div className="actions">
            <button
              className="btn secondary"
              onClick={handleCopy}
              type="button"
            >
              <CopyIcon />
              {copied ? "Copied!" : "Copy link"}
            </button>

            <button
              className="btn primary moved"
              onClick={handleGo}
              type="button"
            >
              Go now
              <ArrowRightIcon />
            </button>
          </div>

          <p className="hint">
            Redirecting in <strong>{secondsLeft}</strong> second
            {secondsLeft === 1 ? "" : "s"}…
          </p>
        </div>
      </div>
    </div>
  );
};

export default TalentRedirection;
